/** One captured write that waits for a connection. */
export interface QueuedRequest {
  id: string;
  createdAt: string;
  method: string;
  url: string;
  headers: Record<string, string>;
  body: string | null;
  /** Short name for the list, e.g. "Behandlung". */
  kind: QueueKind;
  attempts: number;
  /** Set when the server rejected the entry; it then waits for a decision. */
  failure: string | null;
  /**
   * The user who captured the entry. It is only sent in their session, never
   * with somebody else's cookie on a shared device (missing in old entries).
   */
  owner?: string | null;
}

export type QueueKind =
  | 'treatment'
  | 'feeding'
  | 'miteCount'
  | 'note'
  | 'taskComplete'
  | 'marketSale';

/** Storage of the queue; IndexedDB in the browser, in memory in tests. */
export interface QueueStore {
  all(): Promise<QueuedRequest[]>;
  put(item: QueuedRequest): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryQueueStore implements QueueStore {
  private items = new Map<string, QueuedRequest>();
  async all() {
    return [...this.items.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  async put(item: QueuedRequest) {
    this.items.set(item.id, item);
  }
  async remove(id: string) {
    this.items.delete(id);
  }
  async clear() {
    this.items.clear();
  }
}

const DB_NAME = 'swarm-offline';
const STORE = 'queue';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export class IndexedDbQueueStore implements QueueStore {
  private db: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    this.db ??= new Promise((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1);
      open.onupgradeneeded = () => open.result.createObjectStore(STORE, { keyPath: 'id' });
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error);
    });
    return this.db;
  }

  private async store(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    return (await this.open()).transaction(STORE, mode).objectStore(STORE);
  }

  async all() {
    const items = await request(
      (await this.store('readonly')).getAll() as IDBRequest<QueuedRequest[]>,
    );
    return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  async put(item: QueuedRequest) {
    await request((await this.store('readwrite')).put(item));
  }
  async remove(id: string) {
    await request((await this.store('readwrite')).delete(id));
  }
  async clear() {
    await request((await this.store('readwrite')).clear());
  }
}
